import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useId } from "react";
import GfgButton from "@/components/ui/GfgButton";
import { COPY } from "@/constants/campaignContacts";
import type { DepositPreference, RecipientAdditionsQuote } from "@/interfaces/campaignContacts";
import { depositModeOptions } from "./cardPurchase-helper";
import StepNotice from "./StepNotice";
import type { CreditsShortfallView, DepositModeOption } from "./types";

interface AdditionCreditsPurchaseProps {
  quote: RecipientAdditionsQuote;
  /** What the credits the wallet lacks call for (`creditsShortfallView`), already worded. */
  view: CreditsShortfallView;
  /** The postpaid choice the quote was asked for; the selector shows it while the quote is short. */
  depositPreference: DepositPreference;
  onPickDepositMode: (mode: DepositPreference) => void;
  /** Opens the Buy Credits modal for the funding wallet. */
  onBuy: () => void;
  /** Reads a processing purchase's status, then asks for the quote again. */
  onCheckAgain: () => void;
  /** A request is in flight: the choice and the buttons are held. */
  disabled: boolean;
}

const DEPOSIT_MODE_ICONS: Record<DepositPreference, typeof CreditCardOutlinedIcon> = {
  card: CreditCardOutlinedIcon,
  invoice: ReceiptLongOutlinedIcon,
};

const LEGEND_CLASS = "mt-2 mb-2.5 p-0 text-[11px] font-bold uppercase tracking-[2px] text-dust-ink";

/** One deposit choice, a radio styled as the prototype's `.cx-amend-opt`. */
function DepositModeChoice({
  option,
  name,
  active,
  disabled,
  onPick,
}: {
  option: DepositModeOption;
  name: string;
  active: boolean;
  disabled: boolean;
  onPick: (mode: DepositPreference) => void;
}) {
  const Icon = DEPOSIT_MODE_ICONS[option.mode];
  return (
    <label
      className={[
        "flex items-start gap-2 rounded-lg border bg-white px-3 py-2.5 font-lato text-rodeo-black",
        "transition-colors has-focus-visible:outline-2",
        active
          ? "border-rodeo-black shadow-[inset_0_0_0_1px_var(--color-rodeo-black)]"
          : "border-smoke hover:border-rodeo-dust",
        disabled ? "cursor-default opacity-60" : "cursor-pointer",
      ].join(" ")}
    >
      <input
        type="radio"
        name={name}
        className="sr-only"
        checked={active}
        disabled={disabled}
        onChange={() => onPick(option.mode)}
      />
      <Icon sx={{ fontSize: 18, flexShrink: 0, color: "var(--color-dust-ink)" }} />
      <span className="min-w-0">
        <strong className="block text-[13px]">{option.label}</strong>
        <small className="mt-0.5 block text-[11.5px] text-graphite">{option.sub}</small>
      </span>
    </label>
  );
}

/** Postpaid with a short wallet: the deposit top-up is bought as credits (default) or invoiced. */
function DepositModePicker({
  options,
  value,
  disabled,
  onPick,
}: {
  options: readonly DepositModeOption[];
  value: DepositPreference;
  disabled: boolean;
  onPick: (mode: DepositPreference) => void;
}) {
  const name = useId();
  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className={LEGEND_CLASS}>{COPY.addWizard.depositModes.title}</legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {options.map((option) => (
          <DepositModeChoice
            key={option.mode}
            option={option}
            name={name}
            active={option.mode === value}
            disabled={disabled}
            onPick={onPick}
          />
        ))}
      </div>
    </fieldset>
  );
}

/**
 * The view's one control or sentence. Credits that would expire before the close come first: no
 * purchase helps them, so no BUY CREDITS is offered (K13); nor is it without a card to charge.
 */
function ShortfallAction({
  view,
  disabled,
  onBuy,
  onCheckAgain,
}: {
  view: CreditsShortfallView;
  disabled: boolean;
  onBuy: () => void;
  onCheckAgain: () => void;
}) {
  switch (view.kind) {
    case "none":
      return null;
    case "lotExpiry":
      return (
        <StepNotice blocking>
          {view.text} {view.canAddText}
        </StepNotice>
      );
    case "pending":
      return (
        <StepNotice tone="neutral">
          {view.text}{" "}
          <GfgButton
            type="button"
            variant="text"
            className="inline p-0 align-baseline text-[13px] underline"
            disabled={disabled}
            onClick={onCheckAgain}
          >
            {view.checkAgain}
          </GfgButton>
        </StepNotice>
      );
    case "buy":
      return (
        <div className="flex justify-end">
          <GfgButton
            type="button"
            variant="accent"
            startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
            disabled={disabled}
            onClick={onBuy}
          >
            {view.button}
          </GfgButton>
        </div>
      );
    case "askAdmin":
    case "noCard":
      return <StepNotice blocking>{view.text}</StepNotice>;
  }
}

/**
 * GFG-364 QA round 2026-10-14 (R4) — paying for the credits the funding wallet lacks: the postpaid
 * Card / Invoice choice, then BUY CREDITS (JR's Buy Credits modal, opened by the wizard), the
 * purchase still processing, or why buying is not offered. Nothing is charged here; every rule is
 * `creditsShortfall-helper.ts`'s.
 */
export default function AdditionCreditsPurchase({
  quote,
  view,
  depositPreference,
  onPickDepositMode,
  onBuy,
  onCheckAgain,
  disabled,
}: AdditionCreditsPurchaseProps) {
  const options = depositModeOptions(quote);
  if (options.length === 0 && view.kind === "none") return null;
  return (
    <div className="mt-2.5 flex flex-col gap-2.5">
      {options.length > 0 && (
        <DepositModePicker
          options={options}
          value={depositPreference}
          disabled={disabled}
          onPick={onPickDepositMode}
        />
      )}
      <ShortfallAction view={view} disabled={disabled} onBuy={onBuy} onCheckAgain={onCheckAgain} />
    </div>
  );
}
